import { getOrigin } from '../config/siteConfig';
import { gradeLegendRows, getGradeBinIndex, normalizeGradeThresholds } from './gradePalette';
import { mineToScene, sectionPlanes } from './sectionMath';
import { useBlockModelDataStore } from '../store/blockModelDataStore';
import { useGradePaletteStore } from '../store/gradePaletteStore';
import { useSectionStore } from '../store/sectionStore';

const DEFAULT_DENSITY = 2.75;

function pointInsidePlanes(point, planes, eps = 0.02) {
  if (!planes?.length) return true;
  for (let i = 0; i < planes.length; i += 1) {
    if (planes[i].distanceToPoint(point) < -eps) return false;
  }
  return true;
}

/**
 * Tonnes / grade for loaded block models whose cell centres sit inside the
 * current section corridor or box. Object-clip is skipped (centres only).
 */
export function reportBlocksInClip({
  density = DEFAULT_DENSITY,
  sectionState,
  origin = getOrigin(),
  thresholds,
} = {}) {
  const section = sectionState || useSectionStore.getState();
  const bins = normalizeGradeThresholds(thresholds || useGradePaletteStore.getState().thresholds);
  const legend = gradeLegendRows(bins);
  const sg = Number(density);
  const rho = Number.isFinite(sg) && sg > 0 ? sg : DEFAULT_DENSITY;
  const enabled = Boolean(section?.enabled) && section.orientation !== 'object';
  const planes = enabled ? sectionPlanes(section, origin) : [];

  const layers = useBlockModelDataStore.getState().list();
  const byBin = legend.map((row) => ({
    label: row.label,
    css: row.css,
    min: row.min,
    tonnes: 0,
    volume: 0,
    metal: 0,
    cells: 0,
  }));
  let totalVolume = 0;
  let totalTonnes = 0;
  let totalMetal = 0;
  let cells = 0;
  let scanned = 0;
  const layerNames = [];

  layers.forEach((layer) => {
    const packed = layer.packed;
    if (!packed?.count) return;
    layerNames.push(layer.name);
    for (let i = 0; i < packed.count; i += 1) {
      scanned += 1;
      const scene = mineToScene(packed.x[i], packed.y[i], packed.z[i], origin);
      scene.x += Number(layer.meta?.dx) || 0;
      scene.y += Number(layer.meta?.dy) || 0;
      scene.z += Number(layer.meta?.dz) || 0;
      if (planes.length && !pointInsidePlanes(scene, planes)) continue;
      const sx = Math.max(0.01, packed.sx[i]);
      const sy = Math.max(0.01, packed.sy[i]);
      const sz = Math.max(0.01, packed.sz[i]);
      const volume = sx * sy * sz;
      const tonnes = volume * rho;
      const grade = Number(packed.g[i]);
      const g = Number.isFinite(grade) ? grade : 0;
      const idx = getGradeBinIndex(g, bins);
      const bin = byBin[idx] || byBin[byBin.length - 1];
      bin.volume += volume;
      bin.tonnes += tonnes;
      bin.metal += tonnes * g;
      bin.cells += 1;
      totalVolume += volume;
      totalTonnes += tonnes;
      totalMetal += tonnes * g;
      cells += 1;
    }
  });

  const meanGrade = totalTonnes > 0 ? totalMetal / totalTonnes : 0;
  return {
    density: rho,
    clipped: Boolean(planes.length),
    orientation: section?.orientation || '',
    layers: layerNames,
    scanned,
    cells,
    volume: totalVolume,
    tonnes: totalTonnes,
    metal: totalMetal,
    meanGrade,
    ounces: totalMetal / 31.1034768,
    bins: byBin.map((b) => ({
      ...b,
      meanGrade: b.tonnes > 0 ? b.metal / b.tonnes : 0,
    })),
  };
}

export { DEFAULT_DENSITY };
